<template>
  <div class="map-picker">
    <div class="map-search">
      <el-input v-model="searchKeyword" placeholder="搜索地点" clearable @keyup.enter="searchPlace">
        <template #append>
          <el-button @click="searchPlace">搜索</el-button>
        </template>
      </el-input>
    </div>
    <div class="map-toolbar">
      <el-button @click="locateCurrentPosition" :loading="locating">
        <el-icon>
          <Aim />
        </el-icon> 定位到当前位置
      </el-button>
    </div>
    <div ref="mapContainer" class="map-container"></div>

    <div class="selected-info" v-if="selectedAddress">
      <el-icon>
        <Location />
      </el-icon>
      <span>{{ selectedAddress }}</span>
    </div>
    <div class="selected-info" v-else-if="selectedLng && selectedLat">
      <el-icon>
        <Location />
      </el-icon>
      <span>经度: {{ formatCoordinate(selectedLng) }}, 纬度: {{ formatCoordinate(selectedLat) }}</span>
    </div>

    <div class="map-actions">
      <el-button @click="confirmLocation" type="primary">确认选点</el-button>
      <el-button @click="cancel">取消</el-button>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
/* global TMap */
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Location, Aim } from '@element-plus/icons-vue'
import fetchJsonp from 'fetch-jsonp'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      lng: null,
      lat: null,
      address: '',
      province: '',
      city: '',
      district: ''
    })
  },
  mapKey: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const mapContainer = ref(null)
const searchKeyword = ref('')
const selectedLng = ref(null)
const selectedLat = ref(null)
const selectedAddress = ref('')
const locating = ref(false)
const mapReady = ref(false)
const reverseGeocoding = ref(false)

let map = null
let marker = null
let searchService = null
let geocoder = null

const addressComponents = ref({
  province: '',
  city: '',
  district: '',
  street: '',
  street_number: ''
})

const formatCoordinate = (coord) => {
  if (coord === null || coord === undefined) return '0.000000'
  const num = typeof coord === 'string' ? parseFloat(coord) : coord
  return isNaN(num) ? '0.000000' : num.toFixed(6)
}

watch(() => props.modelValue, (newVal) => {
  if (newVal && newVal.lng && newVal.lat && mapReady.value) {
    selectedLng.value = typeof newVal.lng === 'string' ? parseFloat(newVal.lng) : newVal.lng
    selectedLat.value = typeof newVal.lat === 'string' ? parseFloat(newVal.lat) : newVal.lat
    updateMarker(selectedLat.value, selectedLng.value)

    if (newVal.address) {
      selectedAddress.value = newVal.address
      addressComponents.value = {
        province: newVal.province || '',
        city: newVal.city || '',
        district: newVal.district || '',
        street: '',
        street_number: ''
      }
    } else {
      reverseGeocode(selectedLat.value, selectedLng.value)
    }
  }
}, { deep: true })

const initMap = () => {
  if (!mapContainer.value) return

  const existingScript = document.querySelector('script[src*="map.qq.com"]')
  if (existingScript) existingScript.remove()

  const script = document.createElement('script')
  script.src = `https://map.qq.com/api/gljs?v=2.exp&key=${props.mapKey}&libraries=service`

  script.onload = () => {
    try {
      let center = new TMap.LatLng(39.908823, 116.397470)

      if (props.modelValue && props.modelValue.lat && props.modelValue.lng) {
        const lat = typeof props.modelValue.lat === 'string' ? parseFloat(props.modelValue.lat) : props.modelValue.lat
        const lng = typeof props.modelValue.lng === 'string' ? parseFloat(props.modelValue.lng) : props.modelValue.lng
        center = new TMap.LatLng(lat, lng)
        selectedLng.value = lng
        selectedLat.value = lat
      }

      map = new TMap.Map(mapContainer.value, {
        center: center,
        zoom: 15,
        mapStyleId: 'style1'
      })

      searchService = new TMap.service.Search()
      geocoder = new TMap.service.Geocoder()

      mapReady.value = true

      if (props.modelValue && props.modelValue.lat && props.modelValue.lng) {
        const lat = typeof props.modelValue.lat === 'string' ? parseFloat(props.modelValue.lat) : props.modelValue.lat
        const lng = typeof props.modelValue.lng === 'string' ? parseFloat(props.modelValue.lng) : props.modelValue.lng
        updateMarker(lat, lng)

        if (props.modelValue.address) {
          selectedAddress.value = props.modelValue.address
          addressComponents.value = {
            province: props.modelValue.province || '',
            city: props.modelValue.city || '',
            district: props.modelValue.district || '',
            street: '',
            street_number: ''
          }
        } else {
          reverseGeocode(lat, lng)
        }
      } else {
        locateCurrentPosition()
      }

      map.on('click', (evt) => {
        const lat = evt.latLng.getLat()
        const lng = evt.latLng.getLng()
        selectedLng.value = lng
        selectedLat.value = lat
        updateMarker(lat, lng)
        reverseGeocode(lat, lng)
      })

      ElMessage.success('地图加载成功')
    } catch (error) {
      console.error('地图初始化错误:', error)
      ElMessage.error('地图加载失败，请刷新重试')
    }
  }

  script.onerror = () => {
    ElMessage.error('地图脚本加载失败，请检查网络')
  }

  document.head.appendChild(script)
}

const updateMarker = (lat, lng) => {
  if (!map) return
  const position = new TMap.LatLng(lat, lng)
  if (marker) {
    marker.setGeometries([{ id: 'selected', position }])
  } else {
    marker = new TMap.MultiMarker({
      map: map,
      geometries: [{ id: 'selected', position }]
    })
  }
  map.setCenter(position)
}

const reverseGeocode = async (lat, lng) => {
  if (!geocoder) {
    selectedAddress.value = `${formatCoordinate(lat)}, ${formatCoordinate(lng)}`
    return
  }

  if (reverseGeocoding.value) return

  reverseGeocoding.value = true

  try {
    const result = await geocoder.getAddress({ location: new TMap.LatLng(lat, lng) })

    if (result && result.status === 0) {
      const addr = result.result
      const comp = addr.address_component || {}

      let province = comp.province || ''
      let city = comp.city || ''
      let district = comp.district || ''

      const municipalities = ['北京市', '上海市', '天津市', '重庆市']
      if (municipalities.includes(province)) {
        if (!city || city === province) city = province
      }

      let fullAddress = ''
      if (addr.formatted_addresses?.recommend) {
        fullAddress = addr.formatted_addresses.recommend
      } else if (addr.formatted_addresses?.standard_address) {
        fullAddress = addr.formatted_addresses.standard_address
      } else if (addr.address) {
        fullAddress = addr.address
      } else {
        const parts = [province, city, district]
        if (addr.address_reference?.town) parts.push(addr.address_reference.town.title)
        if (addr.address_reference?.landmark_l2) parts.push(addr.address_reference.landmark_l2.title)
        fullAddress = parts.filter(v => v).join('')
      }

      selectedAddress.value = fullAddress || `${formatCoordinate(lat)}, ${formatCoordinate(lng)}`

      addressComponents.value = {
        province, city, district,
        street: comp.street || '',
        street_number: comp.street_number || ''
      }
    } else {
      selectedAddress.value = `${formatCoordinate(lat)}, ${formatCoordinate(lng)}`
      addressComponents.value = { province: '', city: '', district: '', street: '', street_number: '' }
    }
  } catch (error) {
    console.error('逆地址解析异常:', error)
    selectedAddress.value = `${formatCoordinate(lat)}, ${formatCoordinate(lng)}`
    addressComponents.value = { province: '', city: '', district: '', street: '', street_number: '' }
  } finally {
    reverseGeocoding.value = false
  }
}

const searchPlace = async () => {
  const keyword = searchKeyword.value.trim()
  if (!keyword) return

  if (!searchService || !geocoder) {
    ElMessage.warning('地图服务暂不可用')
    return
  }

  try {
    const geocodeResult = await geocoder.getLocation({ address: keyword })

    if (geocodeResult && geocodeResult.status === 0 && geocodeResult.result) {
      const loc = geocodeResult.result.location
      if (loc) {
        const lat = loc.lat
        const lng = loc.lng
        selectedLng.value = lng
        selectedLat.value = lat
        updateMarker(lat, lng)
        map.setZoom(14)
        reverseGeocode(lat, lng)
        return
      }
    }

    const bounds = map.getBounds()
    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()
    const boundaryStr = `rectangle(${sw.getLat()},${sw.getLng()},${ne.getLat()},${ne.getLng()})`

    const regionResult = await searchService.searchRegion({
      keyword: keyword,
      boundary: boundaryStr
    })

    if (regionResult && regionResult.status === 0 && regionResult.data && regionResult.data.length > 0) {
      const first = regionResult.data[0]
      const lat = first.location.lat
      const lng = first.location.lng
      selectedLng.value = lng
      selectedLat.value = lat
      updateMarker(lat, lng)

      if (first.address) {
        selectedAddress.value = first.address
        parseAddressFromString(first.address)
      } else {
        reverseGeocode(lat, lng)
      }
    } else {
      ElMessage.warning('未找到相关地点')
    }
  } catch (error) {
    console.error('搜索失败:', error)
    ElMessage.error('搜索失败，请稍后重试')
  }
}

const parseAddressFromString = (addressStr) => {
  const provinceMatch = addressStr.match(/(.+?省|.+?自治区|北京市|上海市|天津市|重庆市)/)
  const cityMatch = addressStr.match(/(.+?市|.+?自治州|.+?地区|.+?盟)/)
  const districtMatch = addressStr.match(/(.+?区|.+?县|.+?旗)/)

  addressComponents.value = {
    province: provinceMatch ? provinceMatch[1] : '',
    city: cityMatch ? cityMatch[1] : '',
    district: districtMatch ? districtMatch[1] : '',
    street: '',
    street_number: ''
  }
}

const locateCurrentPosition = () => {
  locating.value = true
  ElMessage.info('正在定位中...')

  const url = `https://apis.map.qq.com/ws/location/v1/ip?key=${props.mapKey}&output=jsonp`

  fetchJsonp(url)
    .then(response => response.json())
    .then(data => {
      locating.value = false

      if (data.status === 0 && data.result && data.result.location) {
        const lat = data.result.location.lat
        const lng = data.result.location.lng

        console.log('IP定位结果:', { lat, lng, address: data.result.ad_info })

        selectedLng.value = lng
        selectedLat.value = lat
        updateMarker(lat, lng)
        map.setZoom(14)

        // 构建地址信息
        const adInfo = data.result.ad_info || {}
        selectedAddress.value = data.result.address || `${formatCoordinate(lat)}, ${formatCoordinate(lng)}`
        addressComponents.value = {
          province: adInfo.province || '',
          city: adInfo.city || '',
          district: adInfo.district || '',
          street: '',
          street_number: ''
        }

        ElMessage.success('定位成功')
      } else {
        ElMessage.warning('定位失败，请手动选择位置')
        useDefaultLocation()
      }
    })
    .catch(error => {
      console.error('IP定位失败:', error)
      locating.value = false
      useDefaultLocation()
    })
}

const useDefaultLocation = () => {
  const defaultLat = 39.908823
  const defaultLng = 116.397470

  selectedLng.value = defaultLng
  selectedLat.value = defaultLat
  updateMarker(defaultLat, defaultLng)
  reverseGeocode(defaultLat, defaultLng)

  ElMessage.warning('定位失败，已使用默认位置，请手动选择')
}

const confirmLocation = () => {
  if (!selectedLng.value || !selectedLat.value) {
    ElMessage.warning('请先在地图上选择一个点')
    return
  }

  const comp = addressComponents.value
  const result = {
    lng: selectedLng.value,
    lat: selectedLat.value,
    address: selectedAddress.value,
    province: comp.province,
    city: comp.city,
    district: comp.district
  }

  emit('update:modelValue', result)
  emit('confirm', result)
}

const cancel = () => {
  emit('cancel')
}

onMounted(() => {
  initMap()
})

onBeforeUnmount(() => {
  if (map) map.destroy()
})
</script>

<style scoped>
.map-picker {
  display: flex;
  flex-direction: column;
  height: 550px;
}

.map-search {
  margin-bottom: 8px;
}

.map-toolbar {
  margin-bottom: 8px;
}

.map-container {
  flex: 1;
  min-height: 0;
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  background: #f5f7fa;
}

.selected-info {
  padding: 10px 0;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #606266;
  font-size: 14px;
}

.map-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 12px;
}
</style>